import React, { useState, useEffect } from 'react';
import { Line, Doughnut } from 'react-chartjs-2';
import { API_BASE_URL } from '@/utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  ChartOptions 
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, Filler);

// --- CSS STYLES ---
const overviewStyles = `
  :root { --primary-red: #DC2626; --card-bg: #ffffff; --text-dark: #1F2937; }
  .stats-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 24px; margin-bottom: 32px; }
  .stat-card { background: var(--card-bg); border-radius: 20px; padding: 24px; border: 1px solid #FEE2E2; display: flex; align-items: center; box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.05); }
  .stat-icon { width: 50px; height: 50px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-right: 16px; font-size: 20px; color: white; }
  .stat-icon.red-1 { background: linear-gradient(135deg, #EF4444 0%, #B91C1C 100%); }
  .stat-icon.red-2 { background: linear-gradient(135deg, #F87171 0%, #DC2626 100%); }
  .stat-icon.red-3 { background: linear-gradient(135deg, #FECACA 0%, #F87171 100%); color: #7F1D1D; }
  .stat-icon.red-4 { background: linear-gradient(135deg, #7F1D1D 0%, #450A0A 100%); }
  .stat-info h3 { font-size: 24px; font-weight: 800; color: var(--text-dark); margin: 0; }
  .stat-info p { margin: 0; color: #6B7280; font-size: 12px; font-weight: 600; text-transform: uppercase; }
  .charts-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 24px; }
  .chart-card { background: var(--card-bg); border-radius: 24px; padding: 24px; border: 1px solid #FEE2E2; height: 450px; display: flex; flex-direction: column; }
  .chart-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
  .chart-header h3 { margin: 0; font-size: 18px; font-weight: 700; }
  .chart-wrapper { flex: 1; width: 100%; position: relative; min-height: 0; }
  
  /* Filter Buttons */
  .filter-group { display: flex; background: #F3F4F6; padding: 4px; border-radius: 10px; gap: 4px; }
  .filter-btn {
    border: none; background: transparent; padding: 6px 12px; font-size: 12px; font-weight: 600; color: #6B7280;
    border-radius: 8px; cursor: pointer; transition: all 0.2s;
  }
  .filter-btn:hover { color: #1F2937; }
  .filter-btn.active { background: white; color: #DC2626; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

  /* Loading animation */
  .loading-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 70vh; color: #6B7280; }
  .loading-spinner { width: 50px; height: 50px; border: 4px solid #F3F4F6; border-top: 4px solid #DC2626; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

interface DashboardStats {
  totalUsers: number;
  activeCourses: number;
  monthlySales: number;
  totalRevenue: number;
  revenueChart: { label: string; total: number }[]; // Ubah interface agar dinamis
  levelDistribution: { basic: number; intermediate: number; advanced: number; };
}

// Default stats
const defaultStats: DashboardStats = {
  totalUsers: 0,
  activeCourses: 0,
  monthlySales: 0,
  totalRevenue: 0,
  revenueChart: [],
  levelDistribution: { basic: 0, intermediate: 0, advanced: 0 }
};

const Overview: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly'); // State Filter
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token'); 
        // Tambahkan query param ?period=...
        const response = await fetch(`${API_BASE_URL}/admin/overview?period=${filter}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          const backendData = result.data;
          
          setStats({
            totalUsers: Number(backendData.totalUsers || 0),
            activeCourses: Number(backendData.activeCourses || 0),
            monthlySales: Number(backendData.monthlySales || 0),
            totalRevenue: Number(backendData.totalRevenue || 0), 
            // Data chart langsung pakai dari backend (sudah format label & total)
            revenueChart: Array.isArray(backendData.revenueChart) 
              ? backendData.revenueChart 
              : [],
            levelDistribution: {
              basic: Number(backendData.levelDistribution?.basic || 0),
              intermediate: Number(backendData.levelDistribution?.intermediate || 0),
              advanced: Number(backendData.levelDistribution?.advanced || 0)
            }
          });
        }
      } catch (error: any) {
        console.error("Gagal mengambil data:", error);
        setError("Gagal terhubung ke server backend.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [filter]); // Re-fetch saat filter berubah

  // Formatter Rupiah
  const formatRupiah = (number: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(number);
  };

  // Data untuk line chart (Dinamis berdasarkan API)
  const chartData = {
    labels: stats.revenueChart.map(item => item.label), // Label dinamis (Jan, 01 Jan, 2024, dll)
    datasets: [{
      label: 'Pendapatan',
      data: stats.revenueChart.map(item => item.total),
      borderColor: '#DC2626',
      backgroundColor: (context: any) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, 'rgba(220, 38, 38, 0.2)');
        gradient.addColorStop(1, 'rgba(220, 38, 38, 0)');
        return gradient;
      },
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: '#DC2626',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    }],
  };

  // Data untuk doughnut chart
  const levelData = {
    labels: ['Basic', 'Intermediate', 'Advanced'],
    datasets: [{
      data: [
        stats.levelDistribution?.basic || 0,
        stats.levelDistribution?.intermediate || 0,
        stats.levelDistribution?.advanced || 0
      ],
      backgroundColor: ['#FECACA', '#EF4444', '#991B1B'],
      borderWidth: 0,
    }],
  };

  const optionsLine: ChartOptions<'line'> = { 
    responsive: true, 
    maintainAspectRatio: false, 
    plugins: { 
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F2937',
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13, weight: 'bold' },
        callbacks: {
            label: function(context) {
                return formatRupiah(context.parsed.y || 0);
            }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: '#F3F4F6' },
        border: { display: false },
        ticks: {
          font: { size: 11 },
          color: '#9CA3AF',
          callback: function(value) {
            if (typeof value === 'number') {
                if(value >= 1000000) return (value/1000000).toFixed(1) + ' jt';
                if(value >= 1000) return (value/1000).toFixed(0) + ' rb';
            }
            return value;
          }
        }
      },
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11 }, color: '#9CA3AF' }
      }
    }
  };

  const optionsDoughnut: ChartOptions<'doughnut'> = { 
    responsive: true, 
    maintainAspectRatio: false, 
    cutout: '70%', 
    plugins: { 
      legend: { 
        position: 'bottom',
        labels: { padding: 20, font: { size: 12 }, usePointStyle: true }
      } 
    } 
  };

  if (loading && !stats.totalUsers) { // Hanya loading full jika data awal kosong
    return (
      <>
        <style>{overviewStyles}</style>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Memuat data dashboard...</p>
        </div>
      </>
    );
  }

  const displayRevenue = error ? 0 : stats.totalRevenue;

  return (
    <>
      <style>{overviewStyles}</style>
      
      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon red-1"><i className="fas fa-users"></i></div>
          <div className="stat-info">
            <h3>{stats.totalUsers}</h3>
            <p>Total Siswa</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon red-2"><i className="fas fa-graduation-cap"></i></div>
          <div className="stat-info">
            <h3>{stats.activeCourses}</h3>
            <p>Kursus Aktif</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon red-3"><i className="fas fa-shopping-cart"></i></div>
          <div className="stat-info">
            <h3>{stats.monthlySales}</h3>
            <p>Penjualan Bulan Ini</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon red-4"><i className="fas fa-wallet"></i></div>
          <div className="stat-info">
            <h3>{formatRupiah(displayRevenue)}</h3>
            <p>Total Pendapatan</p>
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="charts-container">
        
        {/* LINE CHART DENGAN FILTER */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Tren Pendapatan</h3>
            
            {/* Filter Buttons UI */}
            <div className="filter-group">
                <button 
                    className={`filter-btn ${filter === 'daily' ? 'active' : ''}`} 
                    onClick={() => setFilter('daily')}
                >
                    Harian
                </button>
                <button 
                    className={`filter-btn ${filter === 'weekly' ? 'active' : ''}`} 
                    onClick={() => setFilter('weekly')}
                >
                    Mingguan
                </button>
                <button 
                    className={`filter-btn ${filter === 'monthly' ? 'active' : ''}`} 
                    onClick={() => setFilter('monthly')}
                >
                    Bulanan
                </button>
                <button 
                    className={`filter-btn ${filter === 'yearly' ? 'active' : ''}`} 
                    onClick={() => setFilter('yearly')}
                >
                    Tahunan
                </button>
            </div>
          </div>
          
          <div className="chart-wrapper">
            {loading ? (
                <div style={{height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF'}}>
                    <i className="fas fa-circle-notch fa-spin"></i> Loading...
                </div>
            ) : (
                <Line data={chartData} options={optionsLine} />
            )}
          </div>
        </div>
        
        <div className="chart-card">
          <div className="chart-header">
            <h3>Distribusi Level</h3>
          </div>
          <div className="chart-wrapper">
            <Doughnut data={levelData} options={optionsDoughnut} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Overview;